import React, { useRef, useState } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import { emailsAPI } from '../../services/api';
import { AttachmentRef } from '../../types';
import { useToast } from '../../contexts/ToastContext';

// Files ride on the email itself up to ~7MB in total; the backend turns
// anything above that into a download link on its own. This number is only
// used to show a neutral heads-up — it never blocks sending.
const INLINE_LIMIT_BYTES = 7 * 1024 * 1024;

// Mirrors the server-side allowlist (png, jpeg, webp, gif, pdf, docx, pptx,
// xlsx, csv, txt). The server is still the authority — this only filters the
// OS file picker.
const ACCEPT = [
    'image/png', 'image/jpeg', 'image/webp', 'image/gif',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv', 'text/plain',
    '.png,.jpg,.jpeg,.webp,.gif,.pdf,.docx,.pptx,.xlsx,.csv,.txt',
].join(',');

export const formatFileSize = (bytes: number) => bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

interface PendingUpload {
    id: number;
    name: string;
    size: number;
}

interface AttachmentPickerProps {
    /** Uploaded refs, owned by the parent so it can put them on the payload. */
    attachments: AttachmentRef[];
    /**
     * A React state setter. It MUST accept the updater form: uploads resolve
     * one after another and each has to append to the newest array, not to the
     * one captured when the picker last rendered.
     */
    onChange: React.Dispatch<React.SetStateAction<AttachmentRef[]>>;
    /** Fired whenever an upload starts or the last one finishes. */
    onUploadingChange?: (uploading: boolean) => void;
    disabled?: boolean;
    className?: string;
}

/**
 * "Attach files" button + upload progress rows + removable chips, shared by
 * the compose modal and the reply box. Inline images stay with
 * RichTextEditor; this is only for real file attachments.
 */
const AttachmentPicker: React.FC<AttachmentPickerProps> = ({
    attachments,
    onChange,
    onUploadingChange,
    disabled = false,
    className = '',
}) => {
    const { showToast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const [pending, setPending] = useState<PendingUpload[]>([]);
    const nextId = useRef(0);

    const totalBytes = attachments.reduce((sum, a) => sum + (a.file_size_bytes || 0), 0);
    const overInlineLimit = totalBytes > INLINE_LIMIT_BYTES;

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const picked = Array.from(files);

        const rows: PendingUpload[] = picked.map(f => ({ id: nextId.current++, name: f.name, size: f.size }));
        setPending(prev => [...prev, ...rows]);
        onUploadingChange?.(true);

        // Sequential: keeps the spinner rows readable and is gentler on the
        // upload endpoint than firing every file at once.
        for (let i = 0; i < picked.length; i++) {
            const file = picked[i];
            const row = rows[i];
            try {
                const res = await emailsAPI.uploadAttachment(file);
                onChange(prev => [...prev, res.data]);
            } catch (err: any) {
                // A rejected upload comes back as HTTP 400 with a string `detail`.
                const detail = err?.response?.data?.detail;
                showToast(typeof detail === 'string' ? detail : `Failed to upload ${file.name}`, 'error');
            } finally {
                setPending(prev => prev.filter(p => p.id !== row.id));
            }
        }

        onUploadingChange?.(false);
        if (inputRef.current) inputRef.current.value = '';
    };


    const removeAttachment = (s3Key: string, idx: number) => {
        // No delete endpoint — dropping it from state is all we can do.
        onChange(prev => prev.filter((a, i) => (s3Key ? a.s3_key !== s3Key : i !== idx)));
    };

    const uploading = pending.length > 0;
    const hasAny = attachments.length > 0 || uploading;

    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
            />
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled || uploading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Attach files"
                >
                    <Paperclip className="w-3.5 h-3.5" />
                    <span>Attach files</span>
                </button>
                {hasAny && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {attachments.length} file{attachments.length === 1 ? '' : 's'} · {formatFileSize(totalBytes)} total
                    </span>
                )}
            </div>

            {hasAny && (
                <div className="flex flex-col gap-1.5">
                    {attachments.map((att, idx) => (
                        <div
                            key={att.s3_key || `${att.filename}-${idx}`}
                            className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                            {att.type === 'image'
                                ? <ImageIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                : <FileText className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />}
                            <span className="text-xs text-gray-800 dark:text-gray-200 truncate flex-1 min-w-0">{att.filename}</span>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0">{formatFileSize(att.file_size_bytes || 0)}</span>
                            <button
                                type="button"
                                onClick={() => removeAttachment(att.s3_key, idx)}
                                className="flex-shrink-0 p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                                title={`Remove ${att.filename}`}
                                aria-label={`Remove ${att.filename}`}
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                    {pending.map(p => (
                        <div
                            key={`pending-${p.id}`}
                            className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                            <div className="w-3.5 h-3.5 flex-shrink-0 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1 min-w-0">{p.name}</span>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0">Uploading…</span>
                        </div>
                    ))}
                </div>
            )}

            {overInlineLimit && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Files over 7 MB in total will be sent as download links instead.
                </p>
            )}
        </div>
    );
};

export default AttachmentPicker;
